import type { Ref } from "vue";
import { usePasswordChangeApi } from "./usePasswordChangeApi";

/** パスワード変更 ボタン操作に必要な依存をまとめた型。 */
interface UsePasswordChangeActionsDeps {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  overlay: Ref<boolean>;
  displayConfirmDialog: Ref<boolean>;
  displayCompletedDialog: Ref<boolean>;
  currentPassword: Ref<string>;
  newPassword: Ref<string>;
  confirmNewPassword: Ref<string>;
  validate: () => string | null;
  clearForm: () => void;
}

/**
 * パスワード変更 ボタン操作を扱う composable。
 * 確認・変更・キャンセル・完了後の処理を管理する。
 */
export const usePasswordChangeActions = (
  deps: UsePasswordChangeActionsDeps
) => {
  const {
    errdialog,
    errMessage,
    overlay,
    displayConfirmDialog,
    displayCompletedDialog,
    currentPassword,
    newPassword,
    validate,
    clearForm,
  } = deps;

  const { changePassword, logPasswordChange } = usePasswordChangeApi();

  const extractErrorInfo = (
    err: unknown
  ): { type: string; message: string } => {
    if (!err || typeof err !== "object") {
      return { type: "", message: "" };
    }

    const error = err as {
      name?: string;
      __type?: string;
      message?: string;
    };

    return {
      type: error.name ?? error.__type ?? "",
      message: error.message ?? "",
    };
  };

  /**
   * 「変更手続きへ(確認)」ボタン @Click
   */
  const onClickOpenConfirmDialog = async (): Promise<void> => {
    // バリデーション実行
    const validationError = validate();
    if (validationError) {
      errdialog.value = true;
      errMessage.value = validationError;
      return;
    }

    // 確認ダイアログ表示
    displayConfirmDialog.value = true;
  };

  /**
   * キャンセル @Click
   */
  const onClickConfirmCancel = async (): Promise<void> => {
    // 確認ダイアログ非表示
    displayConfirmDialog.value = false;
  };

  /**
   * パスワード変更を実行する @Click
   * パスワード変更後もログアウトしない（セッション維持）
   */
  const onClickConfirmEntry = async (): Promise<void> => {
    overlay.value = true;
    try {
      // Amplify Auth の updatePassword でパスワード変更を実行
      await changePassword(currentPassword.value, newPassword.value);

      try {
        await logPasswordChange("success");
      } catch (logErr: unknown) {
        console.error("パスワード変更ログ送信エラー:", logErr);
      }

      // 確認ダイアログ非表示
      displayConfirmDialog.value = false;

      // フォームクリア
      clearForm();

      // 完了ダイアログ表示
      // ※ パスワード変更後もログアウトしない（セッション維持）
      displayCompletedDialog.value = true;
    } catch (err: unknown) {
      // 確認ダイアログ非表示
      displayConfirmDialog.value = false;

      const { type, message } = extractErrorInfo(err);
      try {
        await logPasswordChange("failure", type || message);
      } catch (logErr: unknown) {
        console.error("パスワード変更ログ送信エラー:", logErr);
      }

      errdialog.value = true;
      // AWS Cognito APIのエラーは英語で返るため、日本語に変換する
      if (
        type === "NotAuthorizedException" ||
        message === "Incorrect username or password."
      ) {
        errMessage.value = "現在のパスワードが正しくありません";
      } else if (type === "InvalidPasswordException") {
        errMessage.value = "パスワードの形式が不正です";
      } else if (type === "LimitExceededException") {
        errMessage.value =
          "試行回数の上限に達しました。しばらく経ってから再度お試しください";
      } else {
        errMessage.value = "パスワード変更に失敗しました";
        console.error("パスワード変更エラー:", err);
      }
    } finally {
      overlay.value = false;
    }
  };

  /**
   * 完了ダイアログ閉じる @Click
   */
  const onClickCompletedClose = async (): Promise<void> => {
    displayCompletedDialog.value = false;
  };

  return {
    onClickOpenConfirmDialog,
    onClickConfirmCancel,
    onClickConfirmEntry,
    onClickCompletedClose,
  };
};
